import { useCallback, useEffect, useState } from 'react';
import { alPerderLaClave, api, ErrorApi, type ModoPago, type Sesion } from './api';
import { BarraEstado } from './BarraEstado';
import { Cargando } from './componentes';
import { ConectarGextia } from './pantallas/ConectarGextia';
import { Gasto } from './pantallas/Gasto';
import { Historico } from './pantallas/Historico';
import { Login } from './pantallas/Login';
import { Nota, type GastoPintable } from './pantallas/Nota';
import { Notas } from './pantallas/Notas';
import { NuevoGasto } from './pantallas/NuevoGasto';
import { Pendientes } from './pantallas/Pendientes';
import { crearNavegacion, type EstadoHistorial } from './navegacion';

/**
 * Navegación con una pila propia en vez de una librería de rutas.
 *
 * Son cinco pantallas de un solo nivel y la app se abre desde el icono, no por
 * URL compartida; un router traería dependencias y superficie de actualización
 * sin aportar nada. La mecánica vive en `navegacion.ts` —fuera de React y con
 * tests— porque ya se rompió una vez de una forma que solo se veía en un móvil.
 */
type Vista =
  | { nombre: 'notas' }
  | { nombre: 'nota'; ref: string | number; titulo: string }
  | { nombre: 'nuevo-gasto'; notaRef: string | number; modoPago: ModoPago | null }
  | {
      nombre: 'gasto';
      gastoRef: string | number;
      resumen: { description: string; amount: number; categoryName: string };
    }
  | { nombre: 'pendientes' }
  | { nombre: 'historico' };

const RAIZ: Vista = { nombre: 'notas' };

export function App() {
  const [sesion, setSesion] = useState<Sesion | null | 'cargando'>('cargando');

  const cargarSesion = useCallback(async () => {
    try {
      setSesion(await api.sesion());
    } catch (error) {
      // 401 es el estado normal de quien no ha entrado, no un fallo. Y sin red
      // tampoco podemos saberlo: en ambos casos toca la pantalla de entrada.
      if (error instanceof ErrorApi && error.codigo === 'NO_SESSION') {
        setSesion(null);
        return;
      }
      setSesion(null);
    }
  }, []);

  useEffect(() => {
    void cargarSesion();
  }, [cargarSesion]);

  // Si Gextia rechaza la credencial en cualquier punto de la app —cargando
  // notas, subiendo una foto—, se lleva al usuario a reconectarla en vez de
  // dejarle un error rojo que no dice qué hacer.
  useEffect(() => {
    return alPerderLaClave(() => {
      setSesion((actual) =>
        actual && actual !== 'cargando'
          ? {
              ...actual,
              gextiaConectado: false,
              motivo: 'Tu API key ya no es válida. Vuelve a conectarla.',
            }
          : actual,
      );
    });
  }, []);

  // La navegación vive en navegacion.ts, fuera de React y con tests propios.
  // Aquí solo se refleja su pila en el estado para poder pintar.
  const [nav] = useState(() => crearNavegacion<Vista>(RAIZ, window.history));
  const [pila, setPila] = useState<Vista[]>(() => nav.pila());

  const vista = pila[pila.length - 1] ?? RAIZ;

  useEffect(() => {
    function alVolverAtras(evento: PopStateEvent) {
      setPila(nav.alRetroceder(evento.state as EstadoHistorial | null));
    }
    window.addEventListener('popstate', alVolverAtras);
    return () => window.removeEventListener('popstate', alVolverAtras);
  }, [nav]);

  const navegar = useCallback((siguiente: Vista) => setPila(nav.navegar(siguiente)), [nav]);
  const reemplazar = useCallback(
    (siguiente: Vista) => setPila(nav.reemplazar(siguiente)),
    [nav],
  );
  const volver = useCallback(() => nav.volver(), [nav]);

  async function salir() {
    await fetch('/auth/logout', { method: 'POST', credentials: 'same-origin' });
    setSesion(null);
    setPila(nav.reiniciar(RAIZ));
  }

  if (sesion === 'cargando') {
    return (
      <div className="app">
        <Cargando />
      </div>
    );
  }

  if (sesion === null) {
    return (
      <div className="app">
        <Login alEntrar={() => void cargarSesion()} />
      </div>
    );
  }

  if (!sesion.gextiaConectado) {
    return (
      <div className="app">
        <ConectarGextia motivo={sesion.motivo} alConectar={() => void cargarSesion()} />
      </div>
    );
  }

  return (
    <div className="app">
      <BarraEstado alAbrirPendientes={() => navegar({ nombre: 'pendientes' })} />

      {vista.nombre === 'notas' && (
        <Notas
          alAbrirNota={(ref, titulo) => navegar({ nombre: 'nota', ref, titulo })}
          alVerHistorico={() => navegar({ nombre: 'historico' })}
          alSalir={() => void salir()}
        />
      )}

      {vista.nombre === 'nota' && (
        <Nota
          key={String(vista.ref)}
          notaRef={vista.ref}
          nombreInicial={vista.titulo}
          alVolver={volver}
          alAnadirGasto={(modoPago) =>
            navegar({ nombre: 'nuevo-gasto', notaRef: vista.ref, modoPago })
          }
          alAbrirGasto={(gasto: GastoPintable) =>
            navegar({
              nombre: 'gasto',
              gastoRef: gasto.ref,
              resumen: {
                description: gasto.description,
                amount: gasto.amount,
                categoryName: gasto.categoryName,
              },
            })
          }
        />
      )}

      {vista.nombre === 'nuevo-gasto' && (
        <NuevoGasto
          notaRef={vista.notaRef}
          modoPagoNota={vista.modoPago}
          alVolver={volver}
          // Del alta se va directo a la cámara: el ticket está en la mano ahora,
          // no dentro de tres pantallas.
          alGuardar={(gastoRef, resumen) =>
            reemplazar({
              nombre: 'gasto',
              gastoRef,
              resumen: {
                description: resumen.concepto,
                amount: resumen.importe,
                categoryName: resumen.categoria,
              },
            })
          }
        />
      )}

      {vista.nombre === 'gasto' && (
        <Gasto
          key={String(vista.gastoRef)}
          gastoRef={vista.gastoRef}
          resumen={vista.resumen}
          alVolver={volver}
        />
      )}

      {vista.nombre === 'historico' && (
        <Historico
          alAbrirNota={(id, titulo) => navegar({ nombre: 'nota', ref: id, titulo })}
          alVolver={volver}
        />
      )}

      {vista.nombre === 'pendientes' && <Pendientes alVolver={volver} />}
    </div>
  );
}
